import {Component, OnInit, ViewChild} from '@angular/core';
import {AnalysisService} from "../../../services/analysis.service";
import {TranslocoService} from "@ngneat/transloco";
import {AnalysisComponent} from "../analysis.component";
import {MachineParkFilters} from "../../../model/filters/machine-park-filters";
import {MenuItem, TreeNode} from "primeng/api";
import {HierarchyService} from "../../../services/hierarchy.service";
import {GenericGraphComponent} from "../../shared/generic-graph/generic-graph.component";
import {AppConfig} from "../../../shared/global-variables";
import {ExportFileType} from "../../../model/export-file-type";

@Component({
  selector: 'app-analysis-machine-park',
  templateUrl: './analysis-machine-park.component.html',
  styleUrls: ['./analysis-machine-park.component.scss']
})
export class AnalysisMachineParkComponent implements OnInit {

  @ViewChild('distributionByCountryChart')
  public distributionByCountryChart: GenericGraphComponent;

  @ViewChild('distributionByStateChart')
  public distributionByStateChart: GenericGraphComponent;

  @ViewChild('distributionByLocalityChart')
  public distributionByLocalityChart: GenericGraphComponent;

  @ViewChild('machinesByAntiquityChart')
  public machinesByAntiquityChart: GenericGraphComponent;

  @ViewChild('machinesByModelChart')
  public machinesByModelChart: GenericGraphComponent;

  public distributionByCountryGraph: any;
  public distributionByStateGraph: any;
  public distributionByLocalityGraph: any;
  public machinesByAntiquityGraph: any;
  public machinesByModelGraph: any;

  public plotOptionsPie = {pie: {dataLabels: {enabled: false}}};
  public plotOptionsColumnChart: any;
  public pointFormatPieChart: string;
  public pointFormatAntiquityChart: string = '{series.name}: {point.y}';
  public antiquityCategories: string[];

  public loadingData: boolean;
  public filtersVisible: boolean;

  public hierarchy: TreeNode[];
  public organizations: any[];

  public exportVisible: boolean;
  public chartActions: MenuItem[];
  public exportFileType: ExportFileType;

  private readonly colors: string[] = [];

  private currentFilters: MachineParkFilters;
  private exportChart;


  constructor(private analysisService: AnalysisService,
              private i18n: TranslocoService,
              private hierarchyService: HierarchyService) {
    this.currentFilters = new MachineParkFilters();
    this.chartActions = [];
    this.colors = AppConfig.COLORS;
  }

  ngOnInit(): void {
    this.loadData();
    this.loadFilters();
  }

  private loadData(): void {
    this.loadingData = true;
    this.analysisService.getMachineParkData(this.currentFilters).subscribe(response => {
      this.i18n.selectTranslation().subscribe(() => {
        this.buildPieChart(response.distributionByCountry, 'country');
        this.buildPieChart(response.distributionByState, 'state');
        this.buildPieChart(response.distributionByLocality, 'locality');
        this.buildAntiquityChart(response.machinesByAntiquity);
        this.buildPieChart(response.machinesByModel, 'model');
        this.buildChartActions();
        this.loadingData = false;
      });
    });
  }

  private buildPieChart(data: any, nameChart: string) {
    if (!this.pointFormatPieChart) {
      this.pointFormatPieChart = '{series.name}: {point.y} ({point.percentage:.1f} %)<br>'
        + this.i18n.translate('attribute.total') + ': {point.total}';
    }
    const series = [{
      showInLegend: true,
      type: 'pie',
      name: this.i18n.translate('menu.machines'),
      data: []
    }];
    Object.entries(data).forEach(([key, numMachines], index) => {
      series[0].data.push({
        name: key,
        y: numMachines,
        color: this.colors[index]
      });
    });

    if (nameChart === 'country') {
      this.distributionByCountryGraph = series;
    } else if (nameChart === 'state') {
      this.distributionByStateGraph = series;
    } else if (nameChart === 'locality') {
      this.distributionByLocalityGraph = series;
    } else {
      this.machinesByModelGraph = series;
    }
  }

  private buildAntiquityChart(data: any): void {
    this.plotOptionsColumnChart = AnalysisComponent.getPlotOptionsColumnChart();
    const years = this.i18n.translate('time.years');
    this.antiquityCategories = ['0-1 ' + years, '1-3 ' + years, '3-5 ' + years, '> 5 ' + years];
    const antiquityDataSeries = [{data: [], name: this.i18n.translate('menu.machines')}];
    Object.entries(data).forEach(([, numMachines]) => {
      antiquityDataSeries[0].data.push(numMachines);
    });
    const noValues: boolean = antiquityDataSeries[0].data.join() === [0, 0, 0, 0].join();
    this.machinesByAntiquityGraph = noValues ? null : antiquityDataSeries;
  }

  private buildChartActions(): void {
    this.chartActions = [
      {
        label: this.i18n.translate('common.exportXls'),
        icon: 'pi pi-file-excel',
        command: () => this.showExportToXls()
      },
      {
        label: this.i18n.translate('common.downloadPng'),
        icon: 'pi pi-download',
        command: () => this.downloadChart()
      }
    ];
  }

  showExportToXls(): void {
    this.exportVisible = true;
  }

  downloadChart(): void {
    try {
      setTimeout((): void => {
        if (this.exportFileType === ExportFileType.ANALYSIS_MACHINES_BY_ANTIQUITY_CHART || this.exportFileType === ExportFileType.ANALYSIS_MACHINES_BY_MODEL_CHART) {
          this.downloadMachinesChart();
        } else {
          this.downloadDistributionChart();
        }
      });
    } catch (ignored) {
    }
  }

  private downloadMachinesChart(): void {
    if (this.exportFileType === ExportFileType.ANALYSIS_MACHINES_BY_ANTIQUITY_CHART) {
      if (this.machinesByAntiquityChart.chart.axes != undefined) {
        this.exportChart = this.machinesByAntiquityChart.chart;
      }
      this.machinesByAntiquityChart.download(this.exportChart);
    } else {
      if (this.machinesByModelChart.chart.axes != undefined) {
        this.exportChart = this.machinesByModelChart.chart;
      }
      this.machinesByModelChart.download(this.exportChart);
    }
  }

  private downloadDistributionChart(): void {
    if (this.exportFileType === ExportFileType.ANALYSIS_DISTRIBUTION_BY_COUNTRY_CHART) {
      if (this.distributionByCountryChart.chart.axes != undefined) {
        this.exportChart = this.distributionByCountryChart.chart;
      }
      this.distributionByCountryChart.download(this.exportChart);
    } else if (this.exportFileType === ExportFileType.ANALYSIS_DISTRIBUTION_BY_STATE_CHART) {
      if (this.distributionByStateChart.chart.axes != undefined) {
        this.exportChart = this.distributionByStateChart.chart;
      }
      this.distributionByStateChart.download(this.exportChart);
    } else {
      if (this.distributionByLocalityChart.chart.axes != undefined) {
        this.exportChart = this.distributionByLocalityChart.chart;
      }
      this.distributionByLocalityChart.download(this.exportChart);
    }
  }

  private loadFilters(): void {
    this.hierarchyService.getHierarchyDropdown().subscribe(response => {
      this.hierarchy = this.hierarchyService.getHierarchyTreeNodeFilter(response.hierarchyDto)[1];
    });
  }

  applyFilters(): void {
    this.currentFilters.organizationUuids = [];
    if (this.organizations != null && this.organizations.length > 0) {
      this.organizations.forEach(organization => {
        this.currentFilters.organizationUuids.push(organization.data);
      });
    }
    this.loadData();
  }

  resetFilters(): void {
    this.currentFilters = new MachineParkFilters();
    this.organizations = [];
  }

  public getContentExport(): any {
    if (this.exportFileType === ExportFileType.ANALYSIS_DISTRIBUTION_BY_COUNTRY_CHART) {
      return this.distributionByCountryGraph[0].data;
    } else if (this.exportFileType === ExportFileType.ANALYSIS_DISTRIBUTION_BY_STATE_CHART) {
      return this.distributionByStateGraph[0].data;
    } else if (this.exportFileType === ExportFileType.ANALYSIS_DISTRIBUTION_BY_LOCALITY_CHART) {
      return this.distributionByLocalityGraph[0].data;
    } else if (this.exportFileType === ExportFileType.ANALYSIS_MACHINES_BY_ANTIQUITY_CHART) {
      return this.machinesByAntiquityGraph[0].data;
    } else {
      return this.machinesByModelGraph[0].data;
    }
  }

  public readonly ExportFileType = ExportFileType;
}
